<template>
  <ContentSection>
    <!-- OVERVIEW PUBLICATIONS -->
    <!-- TITLE & TEXT -->
    <HeadingAndText :heading="data.heading" :text="data.text" />

    <ClientOnly>
      <Teleport to="#sidebar-filter" :disabled="!lgAndLarger">
        <TaxonomyFilter
          v-if="data.has_filter"
          taxonomy="publishers"
          :terms="data.filter_publishers"
          :filter-all="filterAll"
          :selected-slugs="selectedSlugs"
          :loading="loading"
          @update:filter-all="onAllChange"
          @update:term="onTermChange"
        />
      </Teleport>
    </ClientOnly>

    <!-- SKELETON LOADING -->
    <ul
      v-if="loading"
      class="
        space-y-5
        sm:space-y-7
      "
    >
      <li v-for="i in skeletonCount" :key="i">
        <div class="animate-pulse cursor-wait overflow-hidden rounded-bl-xl">
          <div
            class="
              mb-5 flex items-center gap-5 gap-y-1 rounded-bl-xl border-b
              border-gray-300
            "
          >
            <!-- Date -->
            <div class="h-8 w-30 rounded-bl-lg bg-gray-300" />
            <!-- Publisher -->
            <div class="h-4 w-72 rounded bg-gray-300" />
          </div>
          <div
            class="
              grid
              sm:grid-cols-3
            "
          >
            <!-- Image -->
            <div class="aspect-[3/2] bg-gray-300" />
            <!-- Text -->
            <div
              class="
                p-5
                sm:col-span-2
              "
            >
              <!-- Heading -->
              <div class="mb-2 h-7 w-full rounded bg-gray-300" />
              <!-- Text -->
              <div class="space-y-3">
                <div class="h-3 w-5/6 rounded bg-gray-300" />
                <div class="h-3 w-11/12 rounded bg-gray-300" />
                <div class="h-3 w-1/4 rounded bg-gray-300" />
              </div>
              <!-- Button -->
              <div class="mt-6 h-8 w-20 rounded-bl-xl bg-gray-300" />
            </div>
          </div>
        </div>
      </li>
    </ul>

    <!-- Publications -->
    <TransitionGroup
      v-else
      tag="ul"
      name="list"
      mode="out-in"
      class="
        space-y-5
        sm:space-y-7
      "
    >
      <li
        v-for="entry in entries"
        :key="entry.id"
      >
        <CardPublication :data="entry" />
      </li>
    </TransitionGroup>

    <!-- NO RESULTS -->
    <div v-if="!loading && entries.length === 0" class="text-center">
      <IconMagnifyingGlass
        class="
          mx-auto mb-3 size-6
          sm:mb-5 sm:size-10
        "
      />
      <p>
        {{ $t('no_entries_found') }}
      </p>
    </div>

    <!-- LOAD MORE -->
    <Transition name="fade">
      <AppButton
        v-if="!loading && data.selection.value === 'auto' && parseInt(total) > entries.length"
        variant="primary"
        class="mt-6"
        @click="loadMore"
      >
        {{ $t('load_more') }}
      </AppButton>
    </Transition>
  </ContentSection>
</template>

<script setup lang="ts">
import type { MainComponentsOverviewPublicationsFragment } from '#gql'
import {
  breakpointsTailwind, useBreakpoints,
} from '@vueuse/core'

const props = defineProps<{ data: Enforce<MainComponentsOverviewPublicationsFragment> }>()

const {
  locale, locales,
} = useI18n()

const breakpoints = useBreakpoints(breakpointsTailwind)
const lgAndLarger = breakpoints.greater('lg')

const entries = ref(props.data.entries)
const page = ref(1)
const total = ref(props.data.total)
const loading = ref(false)
const skeletonCount = parseInt(props.data.limit || '8')

// Filter
const {
  filterAll,
  selectedSlugs,
  onAllChange,
  onTermChange,
} = useTaxonomyFilter('publishers', toRef(props.data, 'filter_publishers'))

const GqlInstance = useGql()

function buildQueryOptions(pageNum: number) {
  const limit = parseInt(props.data.limit || '8')

  // Find the locale in the list of available locales by code
  const localeObj = locales.value.find(site => site.code === locale.value) || locales.value[0]

  const categoriesSlugs = props.data.publication_categories?.map(category => category.slug) || []

  let queryOptions: {
    sort: string
    limit: number
    page: number
    site?: string
    filter?: object
    categories?: string[]
  } = {
    sort: 'date desc',
    limit: limit,
    page: pageNum,
    categories: categoriesSlugs,
  }

  let filter: Record<string, object> = {}

  // Filter by selected publishers
  if (selectedSlugs.value.length > 0) {
    const publisherValues = props.data.filter_publishers
      .filter(p => selectedSlugs.value.includes(p.slug))
      .flatMap(p => [p.id, p.slug])
    filter = {
      ...filter,
      publisher: { in: publisherValues },
    }
  }

  // Locale filter
  if (localeObj.isMain) {
    filter = {
      ...filter,
      site: { contains: `\\_${localeObj.languageTag}` },
    }
  } else {
    queryOptions = {
      ...queryOptions,
      site: locale.value,
    }
  }

  if (Object.keys(filter).length > 0) {
    queryOptions = {
      ...queryOptions,
      filter: filter,
    }
  }

  return queryOptions
}

async function fetchEntries(pageNum: number) {
  const queryOptions = buildQueryOptions(pageNum)
  return await GqlInstance('Publications', queryOptions)
}

async function applyFilter() {
  loading.value = true
  page.value = 1
  const { entries: result } = await fetchEntries(1)
  if (!result) return
  entries.value = result.data as typeof entries.value
  total.value = String(result.total)
  loading.value = false
}

// Re-fetch when filter changes
watch(selectedSlugs, applyFilter)

// Apply URL query-param filter after initial render
onMounted(() => {
  if (selectedSlugs.value.length > 0) {
    applyFilter()
  }
})

async function loadMore() {
  page.value += 1
  const { entries: result } = await fetchEntries(page.value)
  if (!result) return

  total.value = String(result.total)

  if (result.data.length === 0) {
    return
  }

  entries.value = entries.value.concat(result.data as typeof entries.value)
}
</script>
